import React, { useState, useEffect } from 'react';
import { Input, Select, Button, Form, Table, InputNumber } from 'antd';
import { Plus, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../../Components/Notification/Notification';
import { ArrowLeftOutlined } from '@ant-design/icons';
const { Option } = Select;

const MakenAanpassenKlasScreen = () => {
  const { get, put, post } = AxiosCaller();
  const navigate = useNavigate();
  const location = useLocation();
  const klas = location.state;
  
  // Ant Design form hook
  const [form] = Form.useForm();

  const [Allergenen, setAllergenen] = useState([]);
  const [KlasTypes, setKlasTypes] = useState([]);
  const [newAllergenRows, setNewAllergenRows] = useState([]); // State for new allergen rows
       const [maxHeight, setMaxHeight] = useState(0);
        
            useEffect(() => {
                const updateHeight = () => {
                    setMaxHeight(0.7 * document.documentElement.clientHeight);
                };
        
                updateHeight(); // Initialiseren bij eerste render
                window.addEventListener("resize", updateHeight); // Updaten bij schermresizing
        
                return () => window.removeEventListener("resize", updateHeight); // Cleanup
            }, []);

  // Fetching data for allergens and class types
  useEffect(() => {
    fetchAllergenen();
    fetchKlasTypes();
    if (klas) {
      updateFields(klas);
    }
  }, [klas]);

  const fetchAllergenen = async () => {
    try {
      const response = await get('/allergeen');
      if (Array.isArray(response.data)) {
        setAllergenen(response.data);
      } else {
        setAllergenen([]);
      }
    } catch (error) {
      console.error('Error fetching allergens:', error);
      openNotificationError("Fout bij ophalen van allergenen", error)

    }
  };

  const fetchKlasTypes = async () => {
    try {
      const response = await get('/klasType');
      if (Array.isArray(response.data)) {
        setKlasTypes(response.data);
      } else {
        setKlasTypes([]);
      }
    } catch (error) {
      console.error('Error fetching class types:', error);
      openNotificationError("Fout bij ophalen van klasTypes", error)
    }
  };

  const updateFields = (klas) => {
    form.setFieldsValue({
      naam: klas.naam,
      lokaal: klas.lokaal,
      aantalStudenten: klas.aantal,
      klasType: klas.klasType?._id,
    });

    const existingAllergens = klas.allergenen.map((allergie) => ({
      allergen: allergie.allergeen,
      aantal: allergie.aantal,
    }));
    setNewAllergenRows(existingAllergens);
  };

  const handleSaveAllergens = async (values) => {
    const allergensArray = newAllergenRows.map((row) => ({
      allergeen: row.allergen._id,
      aantal: row.aantal,
    }));

    const formData = {
      naam: values.naam,
      lokaal: values.lokaal,
      aantal: parseFloat(values.aantalStudenten),
      klasType: values.klasType,
      allergenen: allergensArray,
    };

    try {
      if (klas) {
        await put(`/klas/${klas._id}`, formData);
      } else {
        await post('/klas', formData);
      }

      form.resetFields();
      setNewAllergenRows([]);
      navigate('/Admin/configuratie/klassen');
    } catch (error) {
      console.error('Error saving class data:', error);
      openNotificationError("Fout bij opslaan van klas", error)
    }
  };

  const handleAddAllergieRow = () => {
    setNewAllergenRows([...newAllergenRows, { allergen: '', aantal: 1 }]);
  };


  const handleAllergenChange = (value, index) => {
    const selectedAllergen = Allergenen.find((allergie) => allergie._id === value);
    if (!selectedAllergen) return;
  
    setNewAllergenRows((prevRows) => {
      let updatedRows = [...prevRows];
  
      // Get the old allergen at this index
      const oldAllergen = updatedRows[index]?.allergen;
  
      // Check if the new allergen already exists somewhere else
      const existingIndex = updatedRows.findIndex((row, i) => row.allergen?._id === value && i !== index);
  
      if (existingIndex !== -1) {
        // If allergen already exists, sum up the "aantal" and remove the old row
        updatedRows[existingIndex].aantal = String(
          parseInt(updatedRows[existingIndex].aantal || "0", 10) +
          parseInt(updatedRows[index]?.aantal || "1", 10)
        );
        updatedRows.splice(index, 1); // Remove the duplicate row
      } else {
        // Otherwise, simply update the allergen for the current row
        updatedRows[index] = { ...updatedRows[index], allergen: selectedAllergen };
      }
  
      return updatedRows;
    });
  };
  
  const handleAantalChange = (value, index) => {
    setNewAllergenRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[index].aantal = String(value); // Ensure it remains a string
      return updatedRows;
    });
  };

  return (
    <div className="Screen-container">
      <div className="title-container">
        <div className="flex gap">
          <ArrowLeftOutlined className="back-arrow" onClick={() => navigate('/Admin/configuratie/Klassen')} />
          <h1 className="title">Klas</h1>
        </div>
      </div>

      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSaveAllergens} 
        className="form"
        style={{ overflowY: "auto", maxHeight }}
      >
        <Form.Item 
          label="Naam" 
          name="naam"
          rules={[{ required: true, message: 'Naam is verplicht' }]}
        >
          <Input placeholder="Voer naam in" />
        </Form.Item>

        <Form.Item 
          label="Aantal Studenten" 
          name="aantalStudenten"
          rules={[{ required: true, message: 'Aantal Studenten is verplicht' }]}
        >
          <Input type="number" placeholder="Aantal studenten"  min={1} />
        </Form.Item>

        <Form.Item 
          label="Lokaal" 
          name="lokaal"
          rules={[{ required: true, message: 'Lokaal is verplicht' }]}
        >
          <Input placeholder="Voer lokaal in" />
        </Form.Item>

        <Form.Item 
          label="Klas Type" 
          name="klasType"
          rules={[{ required: true, message: 'Klas Type is verplicht' }]}
        >
          <Select placeholder="Selecteer klas type">
            {KlasTypes.map((type) => (
              <Option key={type._id} value={type._id}>
                {type.naam}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <div style={{ display: 'flex', flexDirection: newAllergenRows.length > 0 ? 'column' : 'row', alignItems: 'flex-start', gap: '10px', marginTop: newAllergenRows.length > 0 ? '20px' : '0px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', width: newAllergenRows.length > 0 ? 'auto' : '100%' }}>
    <Button type="primary" icon={<Plus />} onClick={handleAddAllergieRow}>
      Voeg allergie toe
    </Button>
    {!newAllergenRows.length && (
      <Button type="primary" htmlType="submit">
        {klas ? 'Bewerk klas' : 'Voeg klas toe'}
      </Button>
    )}
  </div>

  {newAllergenRows.length > 0 && (
    <div>
      <h2 className="title">Overzicht Allergenen</h2>
      <Table
        columns={[
          { title: 'Allergenen', dataIndex: 'Allergenen', key: 'Allergenen' },
          { title: 'Aantal', dataIndex: 'Aantal', key: 'Aantal' },
          { title: 'Acties', dataIndex: 'Acties', key: 'Acties' },
        ]}
        dataSource={newAllergenRows.map((row, index) => ({
          key: index,
          Allergenen: (
             <Select
      value={row.allergen?._id}
      onChange={(value) => handleAllergenChange(value, index)}
      placeholder="Selecteer allergie"
      showSearch  
      filterOption={(input, option) =>
        option.children.toLowerCase().includes(input.toLowerCase())  // Customize the search filter
      }
     
    >
      {Allergenen.map((allergeen) => (
        <Option key={allergeen._id} value={allergeen._id}>
          {allergeen.naam}
        </Option>
      ))}
    </Select>
          ),
          Aantal: (
            <InputNumber
              value={row.aantal}
              onChange={(value) => handleAantalChange(value, index)}
              min={1}
              placeholder="Aantal"
            />
          ),
          Acties: (
            <Button onClick={() => setNewAllergenRows(newAllergenRows.filter((_, i) => i !== index))} danger>
              Verwijderen
            </Button>
          ),
        }))}
        rowKey="key"
        pagination={false}
        scroll={{ y: 0.5 * window.innerHeight }}
      />
    </div>
  )}

  {newAllergenRows.length > 0 && (
    <Button type="primary" htmlType="submit">
      {klas ? 'Bewerk klas' : 'Voeg klas toe'}
    </Button>
  )}
</div>

      </Form>
    </div>
  );
}

export default MakenAanpassenKlasScreen;
